import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Download,
  Plus,
  ChevronDown,
  RotateCcw,
  Eye,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  X,
  Trash2,
  Sparkles,
  ClipboardList,
  ShieldOff,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import axiosInstance from '../api/axios';
import { getApiErrorMessage, resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import { ListPageSearchInput } from '../shared/list-page/ListPageSearchInput.tsx';
import { AppDataTable } from '../shared/table/AppDataTable.tsx';
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS, normalizePageSize, readStoredListPageSize, writeStoredListPageSize } from '../shared/table/pagination.ts';
import { Tooltip } from '../admin/components/shared/Tooltip.tsx';
import { DeleteModal } from '../shared/common/index.ts';
import { useJobs } from '../contexts/JobContext';
import { useControlsList, useControlsDashboardMetrics } from '../hooks/controls/useControls.ts';
import { useAuditYear } from '../contexts/AuditYearContext.tsx';
import { controlsQueryKeys } from '../queries/controls/keys.ts';
import { formatFrequencyLabel } from '../features/controls/functional.ts';
import AssociationCountCell from './risks-table/components/AssociationCountCell.tsx';
import { MODULE_KEYS } from '../constants/moduleAccess.ts';
import ModuleAuditDownloadButton from './shared/ModuleAuditDownloadButton.tsx';

/** Sort keys for Controls table (match backend sort keys / transformed row field names). */
const CONTROL_SORT_KEY = {
  DISPLAY_ID: 'control_id',
  NAME: 'title',
  STATUS: 'status',
  FREQUENCY: 'frequency',
  TYPE: 'type',
  SOX_PROCESS: 'soxProcess',
  SIGNIFICANCE: 'significance',
  TEST_EXECUTION_COUNT: 'test_execution_count',
  LAST_EDITED: 'last_edited'
};

const EMPTY_CONTROLS_ROWS = [];

const SEEDED_CONTROL_DELETE_DISABLED_REASON = 'General ITGC control cannot be deleted.';

/** Optional list columns — hidden until enabled via the gear menu. Existing defaults stay visible. */
const CONTROLS_DEFAULT_COLUMN_VISIBILITY = {
  clientControlId: false,
  controlWording: false,
  objective: false,
  controlNature: false,
  mrcControl: false,
  department: false,
  controlOwner: false,
  keyReports: false,
  externalAuditorReliance: false,
  assertions: false,
  source: false,
  domain: false,
  systems: false,
};

/** True when the column is shown (TanStack: missing key = visible). */
function isControlExportColumnVisible(columnVisibility, columnId) {
  return columnVisibility?.[columnId] !== false;
}

function formatControlExportYesNo(value) {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return '';
}

function formatControlExportFrequency(raw) {
  if (!raw || raw === 'Not set') return '';
  return formatFrequencyLabel(String(raw));
}

function formatControlExportClassification(raw) {
  const value = String(raw ?? '').trim().toLowerCase();
  if (value === 'itgc') return 'ITGC';
  if (value === 'business') return 'Business';
  return String(raw ?? '').trim();
}

function formatControlExportSignificance(raw) {
  const value = String(raw || '').trim().toLowerCase();
  if (!value) return '';
  if (value === 'non-key') return 'Non-Key';
  if (value === 'key') return 'Key';
  return String(raw).trim();
}

function YesNoBadge({ value }) {
  if (value === true) {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
        Yes
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700">
        No
      </span>
    );
  }
  return <span className="text-sm text-gray-500">-</span>;
}

function SourceBadge({ label }) {
  const text = String(label || '').trim();
  if (!text) return <span className="text-sm text-gray-500">-</span>;
  const key = text.toLowerCase();
  let chipClass = 'bg-gray-100 text-gray-700';
  if (key === 'manual') chipClass = 'bg-blue-100 text-blue-700';
  else if (key === 'import') chipClass = 'bg-green-100 text-green-700';
  else if (key === 'ai extraction' || key === 'ai' || key === 'extraction') {
    chipClass = 'bg-purple-100 text-purple-700';
  }
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium max-w-full truncate ${chipClass}`}>
      {text}
    </span>
  );
}

/** Hard-cap list cell text so long fields never expand the horizontal scroll. */
function truncateListCell(value, maxChars = 20) {
  const text = String(value ?? '').trim();
  if (!text) return '-';
  if (text.length <= maxChars) return text;
  return `${text.slice(0, maxChars)}...`;
}

function Controls() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasActiveJob } = useJobs();
  const { selectedAuditYearId } = useAuditYear();
  const { data: dashboardMetrics } = useControlsDashboardMetrics();
  const [selectedControls, setSelectedControls] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState(CONTROLS_DEFAULT_COLUMN_VISIBILITY);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterFrequency, setFilterFrequency] = useState('all');
  const [filterSignificance, setFilterSignificance] = useState('all');
  const [filterClassification, setFilterClassification] = useState('all');
  const [sortState, setSortState] = useState({ key: null, dir: 'asc' });
  const sortKey = sortState.key;
  const sortDir = sortState.dir;
  const [deleteModal, setDeleteModal] = useState({ open: false, controlIds: [], controlNames: [] });
  const [deleting, setDeleting] = useState(false);
  const [controlsPage, setControlsPage] = useState(1); // 1-based
  const [controlsPageSize, setControlsPageSize] = useState(readStoredListPageSize);

  const listParams = useMemo(
    () => ({
      page: controlsPage,
      pageSize: controlsPageSize,
      search: debouncedSearchQuery,
      filterType,
      filterStatus,
      filterFrequency,
      filterSignificance,
      filterClassification,
      sortKey,
      sortDir,
      auditYearId: selectedAuditYearId ?? null,
    }),
    [
      controlsPage,
      controlsPageSize,
      debouncedSearchQuery,
      filterType,
      filterStatus,
      filterFrequency,
      filterSignificance,
      filterClassification,
      sortKey,
      sortDir,
      selectedAuditYearId,
    ]
  );

  const { data: listData, isLoading, isError } = useControlsList(listParams);

  const controlsData = useMemo(
    () => listData?.rows ?? EMPTY_CONTROLS_ROWS,
    [listData?.rows]
  );
  const selectedIncludesSeededControl = useMemo(
    () =>
      selectedControls.some((id) =>
        Boolean(controlsData.find((control) => control.id === id)?.isSeeded)
      ),
    [selectedControls, controlsData]
  );
  const totalItems = listData?.totalItems ?? 0;
  const totalPages = listData?.totalPages ?? 1;
  const hasExactTotal = listData?.hasExactTotal ?? true;
  const loading = isLoading;
  const error = isError ? 'Failed to load controls' : null;

  const rowHasActiveJob = useCallback(
    (row) => hasActiveJob(row.id) || (row.controlId ? hasActiveJob(row.controlId) : false),
    [hasActiveJob]
  );

  // Reset to first page when filters/debounced search/sort change
  useEffect(() => {
    setControlsPage(1);
  }, [debouncedSearchQuery, filterType, filterStatus, filterFrequency, filterSignificance, filterClassification, sortKey, sortDir]);

  // Badge component for Type
  const TypeBadge = ({ value }) => {
    const getStyles = () => {
      switch (value) {
        case 'Manual':
          return 'bg-blue-100 text-blue-700';
        case 'Automated':
          return 'bg-green-100 text-green-700';
        default:
          return 'bg-gray-100 text-gray-700';
      }
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium max-w-full truncate ${getStyles()}`}>
        {value}
      </span>
    );
  };

  // Badge component for Status (uses effective status — aligned with detail page)
  const StatusBadge = ({ status }) => {
    if (status === 'draft') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-amber-100 text-amber-700">
          Draft
        </span>
      );
    }
    if (status === 'active') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600">
        —
      </span>
    );
  };

  useEffect(() => {
    setControlsPage((prev) => Math.min(Math.max(1, prev), Math.max(1, totalPages)));
  }, [totalPages]);

  const handleSortClick = useCallback((key) => {
    setSortState((prev) =>
      prev.key === key
        ? { key: prev.key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    );
  }, []);

  const handleResetFilters = useCallback(() => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setFilterType('all');
    setFilterStatus('all');
    setFilterFrequency('all');
    setFilterSignificance('all');
    setFilterClassification('all');
    setSortState({ key: null, dir: 'asc' });
    setControlsPage(1);
  }, []);

  const renderSortIcon = useCallback(
    (key) => {
      if (sortKey !== key) {
        return <ArrowUpDown className="h-3 w-3 text-gray-400 shrink-0" aria-hidden />;
      }
      return sortDir === 'asc'
        ? <ArrowUp className="h-3 w-3 text-blue-600 shrink-0" aria-hidden />
        : <ArrowDown className="h-3 w-3 text-blue-600 shrink-0" aria-hidden />;
    },
    [sortKey, sortDir]
  );

  const handleExport = () => {
    if (selectedControls.length === 0) {
      return;
    }

    // Filter the selected controls from the current page
    const selectedData = controlsData.filter(control => selectedControls.includes(control.id));

    const colVisible = (columnId) => isControlExportColumnVisible(columnVisibility, columnId);

    // Columns follow gear visibility (defaults + any user-enabled columns). Skip Actions.
    const excelData = selectedData.map((control) => {
      const row = {};
      if (colVisible('displayId')) {
        row['Control ID'] = control.displayId || '';
      }
      if (colVisible('name')) {
        row['Control Name'] = control.name || '';
      }
      if (colVisible('status')) {
        row['Status'] = control.effectiveStatus || '';
      }
      if (colVisible('frequency')) {
        row['Frequency'] = formatControlExportFrequency(control.frequency);
      }
      if (colVisible('type')) {
        row['Type'] = control.type || '';
      }
      if (colVisible('classification')) {
        row['Classification'] = formatControlExportClassification(control.classification);
      }
      if (colVisible('significance')) {
        row['Significance'] = formatControlExportSignificance(control.significance);
      }
      if (colVisible('riskCount')) {
        row['Risks'] = control.riskCount ?? 0;
      }
      if (colVisible('testExecutionCount')) {
        row['Test Runs'] = control.testExecutionCount ?? 0;
      }
      if (colVisible('lastEdited')) {
        row['Last Edited'] = control.lastEdited || '';
      }
      if (colVisible('clientControlId')) {
        row['Client Control Id'] = control.clientControlId || '';
      }
      if (colVisible('controlWording')) {
        row['Control Wording'] = control.controlWording || '';
      }
      if (colVisible('objective')) {
        row['Objective'] = control.objective || '';
      }
      if (colVisible('controlNature')) {
        row['Control Nature'] = control.controlNature || '';
      }
      if (colVisible('mrcControl')) {
        row['MRC'] = formatControlExportYesNo(control.mrcControl);
      }
      if (colVisible('department')) {
        row['Department'] = control.department || '';
      }
      if (colVisible('controlOwner')) {
        row['Control Owner'] = control.controlOwner || '';
      }
      if (colVisible('keyReports')) {
        row['Key Reports'] = control.keyReportsLabel || '';
      }
      if (colVisible('externalAuditorReliance')) {
        row['External auditor reliance'] = formatControlExportYesNo(control.externalAuditorReliance);
      }
      if (colVisible('assertions')) {
        const values = Array.isArray(control.assertions) ? control.assertions : [];
        row['Assertions'] = values.length > 0 ? values.join(', ') : '';
      }
      if (colVisible('source')) {
        row['Source'] = control.sourceLabel || control.source || '';
      }
      if (colVisible('domain')) {
        row['Domain'] =
          control.classification === 'itgc' ? control.domainLabel || '' : '';
      }
      if (colVisible('systems')) {
        row['Systems'] =
          control.classification === 'itgc' ? control.systemsLabel || '' : '';
      }
      return row;
    });

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    const colWidthByHeader = {
      'Control ID': 15,
      'Control Name': 35,
      Status: 12,
      Frequency: 15,
      Type: 12,
      Classification: 14,
      Significance: 12,
      Risks: 10,
      'Test Runs': 12,
      'Last Edited': 12,
      'Client Control Id': 16,
      'Control Wording': 40,
      Objective: 30,
      'Control Nature': 16,
      MRC: 8,
      Department: 18,
      'External auditor reliance': 22,
      Assertions: 30,
      Source: 14,
      Domain: 18,
      Systems: 30,
    };
    const headers = excelData.length > 0 ? Object.keys(excelData[0]) : [];
    worksheet['!cols'] = headers.map((header) => ({ wch: colWidthByHeader[header] || 18 }));

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'SOX Controls');

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `SOX_Controls_Export_${timestamp}.xlsx`;

    // Export to file
    XLSX.writeFile(workbook, filename);
  };

  const handleNewControl = () => {
    // Navigate to new control page (to be created later)
    navigate('/sox-controls/new');
  };

  const handleDeleteClick = useCallback((controlIds) => {
    const deletableIds = controlIds.filter((id) => {
      const row = controlsData.find((c) => c.id === id);
      return row && !row.isSeeded;
    });
    if (deletableIds.length === 0) return;
    const controlNames = controlsData
      .filter(c => deletableIds.includes(c.id))
      .map(c => c.name || c.displayId);
    setDeleteModal({ open: true, controlIds: deletableIds, controlNames });
  }, [controlsData]);

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    const userHash = localStorage.getItem('userHash');

    try {
      // Delete each control
      for (const controlId of deleteModal.controlIds) {
        await axiosInstance.delete(`/api/sox-controls/${controlId}`, {
          headers: { 'Authorization': userHash || '' }
        });
      }

      await queryClient.invalidateQueries({ queryKey: controlsQueryKeys.all });
      setSelectedControls((prev) => prev.filter((id) => !deleteModal.controlIds.includes(id)));
      setDeleteModal({ open: false, controlIds: [], controlNames: [] });
    } catch (error) {
      console.error('Error deleting controls:', error);
      alert(resolve403ErrorMessage(error, getApiErrorMessage(error, 'Failed to delete controls. Please try again.')));
    } finally {
      setDeleting(false);
    }
  };

  const tablePagination = useMemo(
    () => ({
      pageIndex: Math.max(0, controlsPage - 1),
      pageSize: controlsPageSize,
    }),
    [controlsPage, controlsPageSize]
  );

  const handleTablePaginationChange = useCallback(
    (updater) => {
      const current = {
        pageIndex: Math.max(0, controlsPage - 1),
        pageSize: controlsPageSize,
      };
      const nextRaw = typeof updater === 'function' ? updater(current) : updater;

      const nextPageSize = normalizePageSize(nextRaw.pageSize, DEFAULT_PAGE_SIZE_OPTIONS);

      if (nextPageSize !== controlsPageSize) {
        setControlsPageSize(nextPageSize);
        writeStoredListPageSize(nextPageSize);
        setControlsPage(1);
        return;
      }

      const nextPage = Math.min(
        Math.max(1, nextRaw.pageIndex + 1),
        Math.max(1, totalPages)
      );
      setControlsPage(nextPage);
    },
    [controlsPage, controlsPageSize, totalPages]
  );

  const selectColumn = useMemo(
    () => ({
      id: 'select',
      columnLabel: 'Select',
      widthPercent: 3,
      header: () => {
        const allOnPageSelected =
          controlsData.length > 0 &&
          controlsData.every((row) => selectedControls.includes(row.id));

        return (
          <input
            type="checkbox"
            className="h-4 w-4 cursor-pointer rounded border border-gray-300 text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedControls((prev) => {
                  const next = new Set(prev);
                  controlsData.forEach((row) => next.add(row.id));
                  return Array.from(next);
                });
              } else {
                const pageIds = new Set(controlsData.map((row) => row.id));
                setSelectedControls((prev) => prev.filter((id) => !pageIds.has(id)));
              }
            }}
            checked={allOnPageSelected}
            aria-label="Select all controls on this page"
          />
        );
      },
      cell: ({ row }) => (
        <div
          role="presentation"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            className="h-4 w-4 cursor-pointer rounded border border-gray-300 text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            checked={selectedControls.includes(row.original.id)}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedControls((prev) =>
                  prev.includes(row.original.id) ? prev : [...prev, row.original.id]
                );
              } else {
                setSelectedControls((prev) =>
                  prev.filter((id) => id !== row.original.id)
                );
              }
            }}
            aria-label="Select control"
          />
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    }),
    [controlsData, selectedControls]
  );

  const columns = useMemo(
    () => [
      selectColumn,
      {
        id: 'displayId',
        columnLabel: 'Control ID',
        widthPercent: 5,
        truncate: true,
        truncateLimit: 8,
        header: () => (
          <button
            type="button"
            onClick={() => handleSortClick(CONTROL_SORT_KEY.DISPLAY_ID)}
            className="flex w-full flex-wrap items-center gap-1 hover:text-gray-800 font-medium uppercase text-xs text-left rounded px-0.5 -mx-0.5 leading-tight"
          >
            Control ID
            {renderSortIcon(CONTROL_SORT_KEY.DISPLAY_ID)}
          </button>
        ),
        cell: ({ row }) => row.original.displayId || '-',
        enableSorting: false,
      },
      {
        id: 'name',
        columnLabel: 'Control Name',
        widthPercent: 10,
        truncate: true,
        truncateLimit: 15,
        textBold: true,
        header: () => (
          <button
            type="button"
            onClick={() => handleSortClick(CONTROL_SORT_KEY.NAME)}
            className="flex w-full flex-wrap items-center gap-1 hover:text-gray-800 font-medium uppercase text-xs text-left rounded px-0.5 -mx-0.5 leading-tight"
          >
            Control Name
            {renderSortIcon(CONTROL_SORT_KEY.NAME)}
          </button>
        ),
        cell: ({ row }) => {
          const full = String(row.original.name || '').trim();
          return <span title={full || undefined}>{truncateListCell(full, 20)}</span>;
        },
        enableSorting: false,
      },
      {
        id: 'status',
        columnLabel: 'Status',
        widthPercent: 5,
        header: () => (
          <button
            type="button"
            onClick={() => handleSortClick(CONTROL_SORT_KEY.STATUS)}
            className="flex w-full flex-wrap items-center gap-1 hover:text-gray-800 font-medium uppercase text-xs text-left rounded px-0.5 -mx-0.5 leading-tight"
          >
            Status
            {renderSortIcon(CONTROL_SORT_KEY.STATUS)}
          </button>
        ),
        cell: ({ row }) => (
          <div className="flex items-center gap-1 min-w-0">
            <StatusBadge status={row.original.effectiveStatus} />
            {rowHasActiveJob(row.original) && (
              <span className="px-1 py-0.5 text-[10px] font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-full inline-flex items-center gap-0.5">
                <Sparkles className="h-2.5 w-2.5 animate-pulse shrink-0" />
                AI
              </span>
            )}
          </div>
        ),
        enableSorting: false,
      },
      {
        id: 'frequency',
        columnLabel: 'Frequency',
        widthPercent: 6,
        truncate: true,
        truncateLimit: 12,
        header: () => (
          <button
            type="button"
            onClick={() => handleSortClick(CONTROL_SORT_KEY.FREQUENCY)}
            className="flex w-full flex-wrap items-center gap-1 hover:text-gray-800 font-medium uppercase text-xs text-left rounded px-0.5 -mx-0.5 leading-tight"
          >
            Frequency
            {renderSortIcon(CONTROL_SORT_KEY.FREQUENCY)}
          </button>
        ),
        cell: ({ row }) => {
          const raw = row.original.frequency;
          if (!raw || raw === 'Not set') return '-';
          return formatFrequencyLabel(String(raw));
        },
        enableSorting: false,
      },
      {
        id: 'type',
        columnLabel: 'Type',
        widthPercent: 7,
        header: () => (
          <button
            type="button"
            onClick={() => handleSortClick(CONTROL_SORT_KEY.TYPE)}
            className="flex w-full flex-wrap items-center gap-1 hover:text-gray-800 font-medium uppercase text-xs text-left rounded px-0.5 -mx-0.5 leading-tight"
          >
            Type
            {renderSortIcon(CONTROL_SORT_KEY.TYPE)}
          </button>
        ),
        cell: ({ row }) => <TypeBadge value={row.original.type} />,
        enableSorting: false,
      },
      {
        id: 'classification',
        columnLabel: 'Classification',
        widthPercent: 6,
        header: () => (
          <span className="font-medium uppercase text-xs">Classification</span>
        ),
        cell: ({ row }) => {
          const raw = String(row.original.classification ?? '').trim().toLowerCase();
          const label = raw === 'itgc' ? 'ITGC' : raw === 'business' ? 'Business' : raw || '-';
          const chipClass =
            raw === 'itgc' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700';
          return (
            <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold ${chipClass}`}>
              {label}
            </span>
          );
        },
        enableSorting: false,
      },
      {
        id: 'significance',
        columnLabel: 'Significance',
        widthPercent: 7,
        header: () => (
          <button
            type="button"
            onClick={() => handleSortClick(CONTROL_SORT_KEY.SIGNIFICANCE)}
            className="flex w-full flex-wrap items-center gap-1 hover:text-gray-800 font-medium uppercase text-xs text-left rounded px-0.5 -mx-0.5 leading-tight"
          >
            Significance
            {renderSortIcon(CONTROL_SORT_KEY.SIGNIFICANCE)}
          </button>
        ),
        cell: ({ row }) => {
          const raw = String(row.original.significance || '').trim().toLowerCase();
          if (!raw) {
            return <span className="text-sm text-gray-500">-</span>;
          }
          const label = raw === 'non-key' ? 'Non-Key' : raw === 'key' ? 'Key' : String(row.original.significance);
          const chipClass =
            raw === 'key'
              ? 'bg-red-100 text-red-700'
              : 'bg-gray-100 text-gray-700';
          return (
            <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold ${chipClass}`}>
              {label}
            </span>
          );
        },
        enableSorting: false,
      },
      {
        id: 'riskCount',
        columnLabel: 'Risks',
        widthPercent: 5,
        header: () => (
          <div className="flex justify-center">
            <span className="font-medium uppercase tracking-wider text-xs">Risks</span>
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex justify-center whitespace-nowrap">
            <AssociationCountCell
              count={row.original.riskCount ?? 0}
              onClick={(e) => {
                e.stopPropagation();
                if ((row.original.riskCount ?? 0) > 0) {
                  navigate(`/sox-controls/view/${row.original.displayId}`);
                }
              }}
            />
          </div>
        ),
        enableSorting: false,
      },
      {
        id: 'testExecutionCount',
        columnLabel: 'Test Runs',
        widthPercent: 5,
        truncate: true,
        truncateLimit: 8,
        header: () => (
          <button
            type="button"
            onClick={() => handleSortClick(CONTROL_SORT_KEY.TEST_EXECUTION_COUNT)}
            className="flex w-full flex-wrap items-center gap-1 hover:text-gray-800 font-medium uppercase text-xs text-left rounded px-0.5 -mx-0.5 leading-tight"
          >
            Test Runs
            {renderSortIcon(CONTROL_SORT_KEY.TEST_EXECUTION_COUNT)}
          </button>
        ),
        cell: ({ row }) => row.original.testExecutionCount ?? 0,
        enableSorting: false,
      },
      {
        id: 'lastEdited',
        columnLabel: 'Last Edited',
        widthPercent: 6,
        truncate: true,
        truncateLimit: 12,
        header: () => (
          <button
            type="button"
            onClick={() => handleSortClick(CONTROL_SORT_KEY.LAST_EDITED)}
            className="flex w-full flex-wrap items-center gap-1 hover:text-gray-800 font-medium uppercase text-xs text-left rounded px-0.5 -mx-0.5 leading-tight"
          >
            Last Edited
            {renderSortIcon(CONTROL_SORT_KEY.LAST_EDITED)}
          </button>
        ),
        cell: ({ row }) => row?.original?.lastEdited || '-',
        enableSorting: false,
      },
      {
        id: 'clientControlId',
        columnLabel: 'Client Control Id',
        widthPercent: 7,
        truncate: true,
        truncateLimit: 16,
        header: () => <span className="font-medium uppercase text-xs">Client Control Id</span>,
        cell: ({ row }) => {
          const full = String(row.original.clientControlId || '').trim();
          return <span title={full || undefined}>{truncateListCell(full, 20)}</span>;
        },
        enableSorting: false,
      },
      {
        id: 'controlWording',
        columnLabel: 'Control Wording',
        widthPercent: 14,
        header: () => <span className="font-medium uppercase text-xs">Control Wording</span>,
        cell: ({ row }) => {
          const full = String(row.original.controlWording || '').trim();
          return <span title={full || undefined}>{truncateListCell(full, 20)}</span>;
        },
        enableSorting: false,
      },
      {
        id: 'objective',
        columnLabel: 'Objective',
        widthPercent: 12,
        header: () => <span className="font-medium uppercase text-xs">Objective</span>,
        cell: ({ row }) => {
          const full = String(row.original.objective || '').trim();
          return <span title={full || undefined}>{truncateListCell(full, 20)}</span>;
        },
        enableSorting: false,
      },
      {
        id: 'controlNature',
        columnLabel: 'Control Nature',
        widthPercent: 8,
        truncate: true,
        truncateLimit: 16,
        header: () => <span className="font-medium uppercase text-xs">Control Nature</span>,
        cell: ({ row }) => row.original.controlNature || '-',
        enableSorting: false,
      },
      {
        id: 'mrcControl',
        columnLabel: 'MRC',
        widthPercent: 5,
        header: () => <span className="font-medium uppercase text-xs">MRC</span>,
        cell: ({ row }) => <YesNoBadge value={row.original.mrcControl} />,
        enableSorting: false,
      },
      {
        id: 'department',
        columnLabel: 'Department',
        widthPercent: 8,
        header: () => <span className="font-medium uppercase text-xs">Department</span>,
        cell: ({ row }) => {
          const full = String(row.original.department || '').trim();
          return <span title={full || undefined}>{truncateListCell(full, 20)}</span>;
        },
        enableSorting: false,
      },
      {
        id: 'controlOwner',
        columnLabel: 'Control Owner',
        widthPercent: 8,
        header: () => <span className="font-medium uppercase text-xs">Control Owner</span>,
        cell: ({ row }) => {
          const full = String(row.original.controlOwner || '').trim();
          return <span title={full || undefined}>{truncateListCell(full, 20)}</span>;
        },
        enableSorting: false,
      },
      {
        id: 'keyReports',
        columnLabel: 'Key Reports',
        widthPercent: 10,
        header: () => <span className="font-medium uppercase text-xs">Key Reports</span>,
        cell: ({ row }) => {
          const full = String(row.original.keyReportsLabel || '').trim();
          return <span title={full || undefined}>{truncateListCell(full, 28)}</span>;
        },
        enableSorting: false,
      },
      {
        id: 'externalAuditorReliance',
        columnLabel: 'External auditor reliance',
        widthPercent: 10,
        truncate: true,
        truncateLimit: 12,
        header: () => (
          <span className="font-medium uppercase text-xs">External auditor reliance</span>
        ),
        cell: ({ row }) => <YesNoBadge value={row.original.externalAuditorReliance} />,
        enableSorting: false,
      },
      {
        id: 'assertions',
        columnLabel: 'Assertions',
        widthPercent: 10,
        truncate: true,
        truncateLimit: 28,
        header: () => <span className="font-medium uppercase text-xs">Assertions</span>,
        cell: ({ row }) => {
          const values = Array.isArray(row.original.assertions) ? row.original.assertions : [];
          if (values.length === 0) return '-';
          return values.join(', ');
        },
        enableSorting: false,
      },
      {
        id: 'source',
        columnLabel: 'Source',
        widthPercent: 7,
        header: () => <span className="font-medium uppercase text-xs">Source</span>,
        cell: ({ row }) => (
          <SourceBadge label={row.original.sourceLabel || row.original.source || ''} />
        ),
        enableSorting: false,
      },
      {
        id: 'domain',
        columnLabel: 'Domain',
        widthPercent: 10,
        truncate: true,
        truncateLimit: 28,
        header: () => <span className="font-medium uppercase text-xs">Domain</span>,
        cell: ({ row }) => {
          if (row.original.classification !== 'itgc') return '--';
          return row.original.domainLabel || '-';
        },
        enableSorting: false,
      },
      {
        id: 'systems',
        columnLabel: 'Systems',
        widthPercent: 12,
        truncate: true,
        truncateLimit: 36,
        header: () => <span className="font-medium uppercase text-xs">Systems</span>,
        cell: ({ row }) => {
          if (row.original.classification !== 'itgc') return '--';
          return row.original.systemsLabel || '-';
        },
        enableSorting: false,
      },
      {
        id: 'actions',
        columnLabel: 'Actions',
        widthPercent: 5,
        enableHiding: false,
        header: () => (
          <div className="flex justify-center">
            <span className="text-xs font-medium text-gray-500 uppercase">
              Actions
            </span>
          </div>
        ),
        cell: ({ row }) => {
          const control = row.original;
          const isSeededControl = Boolean(control.isSeeded);
          const deleteButton = (
            <button
              type="button"
              onClick={isSeededControl ? undefined : () => handleDeleteClick([control.id])}
              disabled={isSeededControl}
              className="p-1 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-gray-400"
              title={isSeededControl ? SEEDED_CONTROL_DELETE_DISABLED_REASON : 'Delete control'}
              aria-label={`Delete control ${control.displayId || control.name}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          );
          return (
            <div
              className="flex items-center justify-center gap-1"
              role="presentation"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
                <button
                  type="button"
                  onClick={() => navigate(`/sox-controls/view/${control.displayId}`)}
                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  title="View control"
                  aria-label={`View control ${control.displayId || control.name}`}
                >
                  <Eye className="h-4 w-4" />
                </button>
              {isSeededControl ? (
                <Tooltip content={SEEDED_CONTROL_DELETE_DISABLED_REASON} side="left">
                  <span className="inline-flex">{deleteButton}</span>
                </Tooltip>
              ) : (
                deleteButton
              )}
            </div>
          );
        },
        enableSorting: false,
      },
    ],
    [selectColumn, handleSortClick, renderSortIcon, rowHasActiveJob, navigate, handleDeleteClick]
  );

  const dashboardTotal = dashboardMetrics?.total ?? 0;
  const dashboardRiskUnmapped = dashboardMetrics?.riskUnmapped ?? 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Controls</h1>
            <p className="text-sm text-gray-600 mt-1">Manage and organize your SOX controls across all processes</p>
          </div>

          <div className="flex items-center gap-3">
            <ModuleAuditDownloadButton
              resourceType="sox_control"
              moduleKey={MODULE_KEYS.CONTROLS}
              filenamePrefix="sox_control_audit"
            />
            {/* New Control Button */}
            <button
              onClick={handleNewControl}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>New Control</span>
            </button>
          </div>
        </div>

        {/* Compact dashboard — totals and risk-unmapped count */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="text-left rounded-lg shadow-sm p-4 border border-gray-200 bg-white min-w-0">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-500 leading-snug min-h-[2.5rem]">Controls</p>
              <div className="flex items-center justify-between gap-2">
                <p className="text-2xl font-bold leading-none tabular-nums text-[#4f46e5]">{dashboardTotal}</p>
                <div className="stat-icon flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#e0e7ff] text-[#4f46e5]" aria-hidden="true">
                  <ClipboardList className="h-5 w-5" strokeWidth={2} />
                </div>
              </div>
            </div>
          </div>
          <div className="text-left rounded-lg shadow-sm p-4 border border-gray-200 bg-white min-w-0">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-500 leading-snug min-h-[2.5rem]">Not mapped to risk</p>
              <div className="flex items-center justify-between gap-2">
                <p className="text-2xl font-bold leading-none tabular-nums text-[#c2410c]">{dashboardRiskUnmapped}</p>
                <div className="stat-icon flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#ffedd5] text-[#c2410c]" aria-hidden="true">
                  <ShieldOff className="h-5 w-5" strokeWidth={2} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-4">
            {/* Search Bar */}
            <div className="flex-1">
              <ListPageSearchInput
                isDebounce
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onDebouncedChange={setDebouncedSearchQuery}
                placeholder="Search controls by name, ID, or owner..."
                aria-label="Search controls"
                inputClassName="bg-gray-50"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="appearance-none pl-4 pr-10 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer text-gray-700 font-medium"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
            </div>

            {/* Type Filter */}
            <div className="relative">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="appearance-none pl-4 pr-10 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer text-gray-700 font-medium"
              >
                <option value="all">All Types</option>
                <option value="manual">Manual</option>
                <option value="automated">Automated</option>
                <option value="it-dependent-manual">IT Dependant Manual</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
            </div>

            {/* Frequency Filter */}
            <div className="relative">
              <select
                value={filterFrequency}
                onChange={(e) => setFilterFrequency(e.target.value)}
                className="appearance-none pl-4 pr-10 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer text-gray-700 font-medium"
              >
                <option value="all">All Frequencies</option>
                <option value="annual">Annual</option>
                <option value="quarterly">Quarterly</option>
                <option value="monthly">Monthly</option>
                <option value="weekly">Weekly</option>
                <option value="event-based">Event Based</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
            </div>

            {/* Significance Filter */}
            <div className="relative">
              <select
                value={filterSignificance}
                onChange={(e) => setFilterSignificance(e.target.value)}
                className="appearance-none pl-4 pr-10 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer text-gray-700 font-medium"
              >
                <option value="all">All Significance</option>
                <option value="key">Key</option>
                <option value="non-key">Non-Key</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
            </div>

            {/* Classification Filter */}
            <div className="relative">
              <select
                value={filterClassification}
                onChange={(e) => setFilterClassification(e.target.value)}
                className="appearance-none pl-4 pr-10 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer text-gray-700 font-medium"
              >
                <option value="all">All Classification</option>
                <option value="business">Business</option>
                <option value="itgc">ITGC</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
            </div>

            <Tooltip content="Reset filters" side="left">
              <button
                type="button"
                onClick={handleResetFilters}
                aria-label="Reset filters"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-800"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
          </div>
        </div>

        {/* Bulk Selection Bar */}
        {selectedControls.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-6 py-3 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-blue-900">
                  {selectedControls.length} {selectedControls.length === 1 ? 'item' : 'items'} selected
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleExport}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-md hover:bg-blue-50 transition-colors"
                  >
                    <Download className="h-4 w-4" />
                    Export
                  </button>
                  <button
                    onClick={() => handleDeleteClick(selectedControls)}
                    disabled={selectedIncludesSeededControl}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                    title={
                      selectedIncludesSeededControl
                        ? SEEDED_CONTROL_DELETE_DISABLED_REASON
                        : undefined
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                </div>
              </div>
              <button
                onClick={() => setSelectedControls([])}
                className="p-1 text-blue-700 hover:text-blue-900 transition-colors"
                title="Clear selection"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {/* Controls Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <AppDataTable
            className="[&_.app-table-scroll-wrap]:overflow-x-auto [&_table]:w-full [&_table]:min-w-full [&_th]:px-2 [&_td]:px-2 [&_th]:py-2 [&_td]:py-2 [&_th]:whitespace-normal [&_th]:break-words [&_th]:align-top [&_td]:whitespace-nowrap [&_td]:overflow-hidden"
            mode="server"
            data={controlsData}
            columns={columns}
            showCellTooltips
            loading={loading}
            error={error}
            paginated
            pagination={tablePagination}
            onPaginationChange={handleTablePaginationChange}
            rowCount={totalItems}
            pageCount={totalPages}
            paginationItemLabel="controls"
            showPageSizeSelector
            showColumnVisibilityMenu
            enableColumnReorder
            defaultColumnVisibility={CONTROLS_DEFAULT_COLUMN_VISIBILITY}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            allowColumnOverflowScroll
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            defaultPageSize={DEFAULT_PAGE_SIZE}
            headerStart={
              <h2 className="text-lg font-medium text-gray-900">
                Controls ({hasExactTotal ? totalItems : `${totalItems}+`})
              </h2>
            }
            getRowId={(row, index) =>
              String(row.id ?? row.control_id ?? row.displayId ?? `row-${index}`)
            }
            isRowSelected={(row) => selectedControls.includes(row.id)}
            emptyMessage={
              totalItems === 0
                ? debouncedSearchQuery ||
                  filterType !== 'all' ||
                  filterStatus !== 'all' ||
                  filterFrequency !== 'all' ||
                  filterSignificance !== 'all' ||
                  filterClassification !== 'all'
                  ? 'No controls match your search or filters.'
                  : 'No controls found.'
                : 'No data available'
            }
          />
        </div>
      </div>

      <DeleteModal
        isOpen={deleteModal.open}
        title={`Delete ${deleteModal.controlIds.length === 1 ? 'Control' : 'Controls'}`}
        message={[
          deleteModal.controlIds.length === 1
            ? 'Are you sure you want to delete this control?'
            : `Are you sure you want to delete these ${deleteModal.controlIds.length} controls?`,
          deleteModal.controlNames.length > 0
            ? deleteModal.controlNames.join('\n')
            : '',
          'This action cannot be undone.',
        ]
          .filter(Boolean)
          .join('\n\n')}
        onClose={() => setDeleteModal({ open: false, controlIds: [], controlNames: [] })}
        onConfirm={handleDeleteConfirm}
        isConfirming={deleting}
        confirmLabel={deleting ? 'Deleting...' : 'Delete'}
      />
    </div>
  );
}

export default Controls;
